<template>
  <v-dialog
    :value="isConfigurarAssociacaoFilaTipoSolicitacaoDialogOpen"
    max-width="900px"
    persistent
  >
    <v-card class="pa-sm-8 pa-2">
      <btn-close-dialog :handle-click="closeDialog" />

      <v-card-title class="justify-center text-h5 pt-0">
        {{ dialogTitle }}
      </v-card-title>

      <v-card-text class="mt-5">
        <v-form
          ref="formRef"
          class="multi-col-validation"
          @submit.prevent="handleSubmit"
        >
          <v-row>
            <v-col
              v-if="isNovaAssociacao"
              cols="12"
            >
              <v-select
                v-if="filaId"
                v-model="associacaoFilaTipoSolicitacaoData.tipoSolicitacaoId"
                dense
                outlined
                label="Tipo solicitação"
                placeholder="Tipo solicitação"
                hide-details="auto"
                validate-on-blur
                item-text="nome"
                item-value="id"
                :items="tiposDeSolicitacoesNaoAssociados"
                :rules="[validators.select]"
              />

              <v-select
                v-if="tipoSolicitacaoId"
                v-model="associacaoFilaTipoSolicitacaoData.filaId"
                dense
                outlined
                label="Fila"
                placeholder="Fila"
                hide-details="auto"
                validate-on-blur
                item-text="filaNome"
                item-value="filaId"
                :items="filasNaoAssociadas"
                :rules="[validators.select]"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-card
                outlined
                elevation="4"
              >
                <v-row class="d-flex px-2">
                  <v-col
                    cols="auto"
                    class="d-flex flex-wrap"
                  >
                    <v-checkbox
                      v-model="associacaoFilaTipoSolicitacaoData.aberturaTicket"
                      label="Abertura de ticket"
                      class="mr-4"
                    />

                    <v-checkbox
                      v-model="associacaoFilaTipoSolicitacaoData.atenderTicket"
                      label="Tratamento de ticket"
                    />
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <tipo-solicitacao-sla-form-fields
            :slas="associacaoFilaTipoSolicitacaoData.slas"
            :expansion-panels-abertos="expansionPanelsAbertos"
            :niveis-alerta="niveisAlerta"
            :btn-title="submitBtnTitle"
            :loading="loading"
            :get-is-alerta-valido="getIsAlertaValido"
            @reset="resetFormData"
            @update:slas="handleSubmit"
          />
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue'
import { select } from '@core/utils/validation'
import { computed, ref, watch, watchEffect } from '@vue/composition-api'
import BtnCloseDialog from '@/components/btn-close-dialog/BtnCloseDialog.vue'
import TipoSolicitacaoSlaFormFields from './TipoSolicitacaoSlaFormFields.vue'
import FilaService from '@/services/modules/FilaService'
import TipoSolicitacaoService from '@/services/modules/TipoSolicitacaoService'
import useDialog from '@/composables/useDialog'
import SlaTipoEnum from '@/models/entities/sla/SlaTipoEnum'

export default {
  components: {
    BtnCloseDialog,
    TipoSolicitacaoSlaFormFields,
  },

  emits: ['update:is-configurar-associacao-fila-tipo-solicitacao-dialog-open', 'refetch-data'],

  props: {
    isConfigurarAssociacaoFilaTipoSolicitacaoDialogOpen: {
      type: Boolean,
      required: true,
    },
    associacaoData: {
      type: Object,
      default: null,
    },
    filaId: {
      type: Number,
      default: null,
    },
    tipoSolicitacaoId: {
      type: Number,
      default: null,
    },
  },

  setup(props, { emit }) {
    const niveisAlerta = {
      1: 'Azul <= 20%',
      2: 'Verde > 20% e <= 50%',
      3: 'Laranja > 50% e <= 80%',
      4: 'Vermelho > 80%',
    }

    const blankAssociacaoFilaTipoSolicitacao = {
      tipoSolicitacaoId: 0,
      filaId: 0,
      aberturaTicket: false,
      atenderTicket: false,
      slas: [
        {
          tipoSlaId: SlaTipoEnum.FinalizacaoTicket,
          tempoSlaEmMinutos: 0,
          alertas: [],
        },
      ],
    }

    const associacaoFilaTipoSolicitacaoData = ref(JSON.parse(JSON.stringify(blankAssociacaoFilaTipoSolicitacao)))
    const expansionPanelsAbertos = ref([])

    function closeExpansionPanels() {
      expansionPanelsAbertos.value = []
    }

    function preencherSlas(slas) {
      const slasCopy = JSON.parse(JSON.stringify(slas || []))
      const slasKeys = [SlaTipoEnum.FinalizacaoTicket]

      const slasPendentes = slasKeys.filter(tipoSlaId => !slasCopy.some(it => it.tipoSlaId === tipoSlaId))

      slasPendentes.forEach(tipoSlaId => {
        slasCopy.push({
          tipoSlaId,
          tempoSlaEmMinutos: 0,
          alertas: [],
        })
      })

      slasCopy.sort((a, b) => a.tipoSlaId - b.tipoSlaId)

      return slasCopy
    }

    function preencherSlaAlertas(slaAlertas) {
      const slaAlertasCopy = JSON.parse(JSON.stringify(slaAlertas))
      const alertasKeys = Object.keys(niveisAlerta).map(Number)

      const alertasPendentes = alertasKeys.filter(id => !slaAlertasCopy.some(alerta => alerta.tipoAlertaId === id))

      alertasPendentes.forEach(id => {
        slaAlertasCopy.push({
          tipoAlertaId: id,
          notificarAgente: false,
          notificarGestor: false,
          notificarViaBrowser: false,
          notificarViaBadge: false,
        })
      })

      slaAlertasCopy.sort((a, b) => a.tipoAlertaId - b.tipoAlertaId)

      return slaAlertasCopy
    }

    function resetFilaTipoSolicitacao() {
      associacaoFilaTipoSolicitacaoData.value = JSON.parse(JSON.stringify(props.associacaoData || blankAssociacaoFilaTipoSolicitacao))
      associacaoFilaTipoSolicitacaoData.value.slas = preencherSlas(associacaoFilaTipoSolicitacaoData.value.slas)
      associacaoFilaTipoSolicitacaoData.value.slas = associacaoFilaTipoSolicitacaoData.value.slas.map(it => ({ ...it, alertas: preencherSlaAlertas(it.alertas) }))
    }

    const tiposDeSolicitacoesNaoAssociados = ref([])
    async function getTiposDeSolicitacoesNaoAssociados() {
      if (!props.filaId) return
      tiposDeSolicitacoesNaoAssociados.value = await FilaService.getTiposDeSolicitacoesNaoAssociadosAUmaFila(props.filaId)
    }

    const filasNaoAssociadas = ref([])
    async function getFilasNaoAssociadas() {
      if (!props.tipoSolicitacaoId) return
      filasNaoAssociadas.value = await TipoSolicitacaoService.getFilasNaoAssociadasAUmTipoDeSolicitacao(props.tipoSolicitacaoId)
    }

    const isNovaAssociacao = computed(() => !props.associacaoData)
    const dialogTitle = computed(() => (isNovaAssociacao.value ? 'Criar associação' : 'Atualizar associação'))
    const submitBtnTitle = computed(() => (isNovaAssociacao.value ? 'Associar' : 'Atualizar'))

    watch(() => props.associacaoData, resetFilaTipoSolicitacao, { immediate: true, deep: true })

    watchEffect(() => {
      if (!props.isConfigurarAssociacaoFilaTipoSolicitacaoDialogOpen) return
      if (!isNovaAssociacao.value) return

      if (props.filaId) {
        getTiposDeSolicitacoesNaoAssociados()
      }

      if (props.tipoSolicitacaoId) {
        getFilasNaoAssociadas()
      }
    })

    const { formRef, loading, resetForm, closeDialog, emitRefetchData } = useDialog(
      emit,
      'is-configurar-associacao-fila-tipo-solicitacao-dialog-open',
    )

    function resetFormData() {
      if (isNovaAssociacao.value) {
        resetForm()
      }
      resetFilaTipoSolicitacao()
      closeDialog()
      closeExpansionPanels()
    }

    async function criarAssociacao(filaId, body) {
      try {
        loading.value = true
        await FilaService.associarFilaETipoSolicitacao(filaId, body)
        emitRefetchData()
        resetFormData()
      } finally {
        loading.value = false
      }
    }

    async function atualizarAssociacao(filaId, body) {
      try {
        loading.value = true
        await FilaService.atualizarAssociacaoEntreFilaETipoDeSolicitacao(filaId, body)
        closeExpansionPanels()
        emitRefetchData()
        closeDialog()
      } finally {
        loading.value = false
      }
    }

    function getIsAlertaValido(alerta) {
      const isAtLeastOneNotificationActive = alerta.notificarViaBrowser || alerta.notificarViaBadge
      const isAtLeastOnePersonActive = alerta.notificarAgente || alerta.notificarGestor

      return (isAtLeastOneNotificationActive && isAtLeastOnePersonActive)
    }

    async function getRequestBody(slas) {
      const body = JSON.parse(JSON.stringify(associacaoFilaTipoSolicitacaoData.value))

      body.slas = slas
        .filter(el => el.tipoSlaId)
        .map(sla => ({
          ...sla,
          alertas: sla.alertas.filter(alerta => getIsAlertaValido(alerta)),
        }))

      body.tipoSolicitacaoId = props.tipoSolicitacaoId || body.tipoSolicitacaoId

      return body
    }

    async function handleSubmit(slas) {
      if (loading.value) return

      if (!formRef.value.validate()) {
        Vue.$toast.warning('Preencha corretamente os campos antes de encaminhar')
        return
      }

      const body = await getRequestBody(slas)
      const filaId = props.filaId || body.filaId

      if (isNovaAssociacao.value) {
        await criarAssociacao(filaId, body)
      } else {
        await atualizarAssociacao(filaId, body)
      }
    }

    return {
      niveisAlerta,
      associacaoFilaTipoSolicitacaoData,
      expansionPanelsAbertos,

      isNovaAssociacao,
      dialogTitle,
      submitBtnTitle,

      tiposDeSolicitacoesNaoAssociados,
      filasNaoAssociadas,

      formRef,
      loading,
      closeDialog,
      resetFormData,

      getIsAlertaValido,
      handleSubmit,

      validators: { select },
    }
  },
}
</script>
